import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    CitationMeasuresConstantType,
    CitationColorsConstantType,
    CitationDynamicStyleType,
    CitationMeasures,
    CitationColors
} from "./type";
import {
    getTypeColors
} from "../../helpers/theme/typeColors";
import type {
    Mutable
} from "../../types";
import {
    getFocusRingStyle
} from "../../helpers/theme/state";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    webStyle
} from "../../utils";

export const getCitationMeasures = ({
    touchTargets,
    pointerType,
    typography,
    spaces
}: CitationMeasuresConstantType): CitationMeasures => {
    const height = typography.labelSmallSize.lineHeight + spaces.spacingXxs * 2;

    const touchTarget = pointerType === "coarse" ? touchTargets.coarse : touchTargets.fine;

    const hitSize = Math.max(height, touchTarget);

    return {
        hitHeight: hitSize,
        hitWidth: hitSize,
        height
    };
};

export const getCitationColors = ({
    isDisabled,
    colors,
    isOpen
}: CitationColorsConstantType): CitationColors => {
    const typeColors = getTypeColors({
        isSelected: isOpen,
        variant: "tonal",
        type: "neutral",
        isDisabled,
        colors
    });

    return {
        containerColor: typeColors.containerColor,
        borderColor: typeColors.borderColor,
        titleColor: typeColors.titleColor
    };
};

const stylesheet = StyleSheet.create({
    container: {
        justifyContent: "center",
        alignItems: "center",
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    surface: {
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "row",
        overflow: "visible"
    },
    label: {
        fontVariant: [
            "tabular-nums"
        ]
    }
});

export const useStyles = ({
    currentMeasures,
    isFocusVisible,
    currentColors,
    shapeConfigs,
    isDisabled,
    radiuses,
    borders,
    variant,
    colors,
    spaces
}: CitationDynamicStyleType) => {
    const isNumber = variant === "number";

    const verticalOffset = (currentMeasures.hitHeight - currentMeasures.height) / 2;

    const horizontalOffset = isNumber ? (currentMeasures.hitWidth - currentMeasures.height) / 2 : 0;

    const styles = {
        surface: {
            paddingHorizontal: isNumber ? spaces.spacingXs : spaces.spacingSm,
            backgroundColor: currentColors.containerColor,
            minWidth: currentMeasures.height,
            height: currentMeasures.height,
            ...getCornerStyle({
                radius: radiuses.chip,
                shapeConfigs
            })
        } as Mutable<ViewStyle>,
        container: {
            marginHorizontal: spaces.spacingXxs - horizontalOffset,
            minHeight: currentMeasures.hitHeight,
            minWidth: currentMeasures.hitWidth,
            marginVertical: -verticalOffset
        } as Mutable<ViewStyle>
    };

    Object.assign(styles.surface, getFocusRingStyle({
        colorKey: "neutral",
        isFocusVisible,
        borders,
        colors
    }));

    if(isDisabled) {
        Object.assign(styles.container, webStyle({
            cursor: "not-allowed"
        }));
    }

    return styles;
};
export default stylesheet;
